import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { AuthService } from '../services/auth';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
  avatar?: string;
  provider?: 'email' | 'google' | 'github' | 'apple';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGitHub: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  loginWithSpotify: () => Promise<void>;
  logout: () => void;
  register: (email: string, password: string, name: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  isAuthenticated: boolean;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing Supabase session
    checkAuthStatus();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Don't set loading to false for initial session check
        if (event === 'INITIAL_SESSION') {
          // This event fires when the session is first checked
          return;
        }
        
        if (event === 'SIGNED_IN' && session?.user) {
          await updateUserFromSession(session.user, session);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Silently update user data when token refreshes
          await updateUserFromSession(session.user, session);
        }
        
        // Only set loading false for actual auth state changes, not initial load
        if (event !== 'TOKEN_REFRESHED') {
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const checkAuthStatus = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await updateUserFromSession(session.user, session);
      } else {
        // No session - user is not signed in
        setUser(null);
      }
    } catch (error) {
      // Authentication error - clear user state
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for device detection
  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) return 'Chrome';
    if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) return 'Safari';
    if (ua.indexOf('Firefox') > -1) return 'Firefox';
    if (ua.indexOf('Edg') > -1) return 'Edge';
    return 'Unknown';
  };

  const getOSInfo = () => {
    const ua = navigator.userAgent;
    if (ua.indexOf('Win') > -1) return 'Windows';
    if (ua.indexOf('Mac') > -1) return 'macOS';
    if (ua.indexOf('Linux') > -1) return 'Linux';
    if (ua.indexOf('Android') > -1) return 'Android';
    if (ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) return 'iOS';
    return 'Unknown';
  };

  const updateUserFromSession = async (supabaseUser: any, session?: any) => {
    try {
      
      // For OAuth users, also check user metadata for name and avatar
      const oauthName = supabaseUser.user_metadata?.full_name || 
                       supabaseUser.user_metadata?.name ||
                       `${supabaseUser.user_metadata?.first_name || ''} ${supabaseUser.user_metadata?.last_name || ''}`.trim();
      
      const oauthAvatar = supabaseUser.user_metadata?.avatar_url || 
                         supabaseUser.user_metadata?.picture;
      
      // Immediately set user with OAuth data - don't wait for profile
      const userObj = {
        id: supabaseUser.id,
        email: supabaseUser.email!,
        name: oauthName || supabaseUser.email?.split('@')[0] || 'User',
        role: 'user' as 'user' | 'admin',
        avatar: oauthAvatar ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(oauthName || supabaseUser.email?.split('@')[0] || 'User')}&background=0891b2&color=fff`,
        provider: (supabaseUser.app_metadata?.provider || 'email') as 'email' | 'google' | 'github' | 'apple'
      };
      
      setUser(userObj);
      
      // Note: Spotify sync is handled in AuthCallbackPage where provider tokens are available
      // Provider tokens are only available immediately after OAuth login, not in regular sessions
      if (supabaseUser.app_metadata?.provider === 'spotify') {
        console.log('Spotify user detected. Sync happens during OAuth callback.');
      }
      
      // Fetch profile asynchronously in the background - don't block the UI
      AuthService.getProfile(supabaseUser.id)
        .then(profile => {
          if (profile) {
            // Update user with profile data if available
            setUser(prev => ({
              ...prev!,
              name: profile.full_name || prev!.name,
              role: (profile.role || 'user') as 'user' | 'admin',
              avatar: profile.avatar_url || prev!.avatar
            }));
          } else if (supabaseUser.app_metadata?.provider !== 'email' && oauthName) {
            // If no profile exists for OAuth user, create one in background
            AuthService.updateProfile(supabaseUser.id, {
              full_name: oauthName,
              avatar_url: oauthAvatar
            }).catch(_err => {
              // Silently handle profile creation errors
            });
          }
        })
        .catch(_err => {
          // Silently handle profile fetch errors - we already have user data
        });
    } catch (error) {
      // Fallback to basic user info with OAuth metadata
      const oauthName = supabaseUser.user_metadata?.full_name || 
                       supabaseUser.user_metadata?.name ||
                       supabaseUser.email?.split('@')[0] || 'User';
      
      const fallbackUser = {
        id: supabaseUser.id,
        email: supabaseUser.email!,
        name: oauthName,
        role: 'user' as const,
        avatar: supabaseUser.user_metadata?.avatar_url || 
                supabaseUser.user_metadata?.picture ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(oauthName)}&background=0891b2&color=fff`,
        provider: (supabaseUser.app_metadata?.provider || 'email') as 'email' | 'google' | 'github' | 'apple'
      };
      
      setUser(fallbackUser);
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const data = await AuthService.signIn(email, password);
      
      if (data.user) {
        try {
          // For email login, we don't have provider tokens
          await updateUserFromSession(data.user, null);
          
          // Track user session
          const userAgent = navigator.userAgent;
          await supabase.rpc('upsert_user_session', {
            p_user_id: data.user.id,
            p_user_agent: userAgent,
            p_device_info: {
              device_type: /Mobile|Android|iPhone/i.test(userAgent) ? 'mobile' : 'desktop',
              browser: getBrowserInfo(),
              os: getOSInfo()
            }
          });
        } catch (sessionError) {
          console.error('Session tracking error:', sessionError);
        }
        
        toast.success('Successfully logged in!');
        
        // Wait a bit longer for React state updates to complete
        setTimeout(() => {
          navigate('/dashboard');
        }, 250);
      } else {
        toast.error('Login failed - no user returned');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string) => {
    setLoading(true);
    try {
      const data = await AuthService.signUp(email, password, { full_name: name });
      
      if (data.user) {
        // Check if email is already confirmed (auto-confirm is enabled in dev)
        if (data.user.email_confirmed_at) {
          // Auto-confirmed, update user state and navigate
          try {
            await updateUserFromSession(data.user, null);
          } catch (sessionError) {
          }
          
          toast.success('Account created successfully!');
          // Add small delay to ensure session is established
          setTimeout(() => {
            navigate('/dashboard');
          }, 250);
        } else {
          // Email confirmation required
          toast.success('Account created successfully! Please check your email to verify your account.');
        }
      } else {
        toast.error('Registration failed - no user returned');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setLoading(true);
    try {
      // Use Supabase client's built-in OAuth
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });
      
      if (error) {
        console.error('Google OAuth error:', error);
        throw error;
      }
      
      // The OAuth flow will redirect the user, so we don't need to do anything else here
      // The loading state will be cleared when the user returns from OAuth
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Google login failed';
      console.error('Google login error:', errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const loginWithGitHub = async () => {
    setLoading(true);
    try {
      // Use Supabase client's built-in OAuth
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'read:user user:email'
        }
      });
      
      if (error) {
        console.error('GitHub OAuth error:', error);
        throw error;
      }
      
      // The OAuth flow will redirect the user, so we don't need to do anything else here
      // The loading state will be cleared when the user returns from OAuth
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'GitHub login failed';
      console.error('GitHub login error:', errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const loginWithApple = async () => {
    setLoading(true);
    try {
      await AuthService.signInWithApple();
      // Redirect will be handled by OAuth flow
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Apple login failed';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const loginWithSpotify = async () => {
    setLoading(true);
    try {
      // Spotify requires explicit scopes for email and user data access
      // user-read-email is REQUIRED for Supabase to get the email address
      // Other scopes are for the personalized event recommendations feature
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'spotify',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'user-read-email user-read-private user-top-read user-follow-read user-read-recently-played user-library-read'
        }
      });
      
      if (error) {
        console.error('Spotify OAuth error:', error);
        throw error;
      }
      
      // OAuth flow will redirect to Spotify, so no need to handle success here
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Spotify login failed';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await AuthService.signOut();
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Error logging out');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setLoading(true);
    try {
      await AuthService.resetPassword(email);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email';
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (newPassword: string) => {
    setLoading(true);
    try {
      await AuthService.updatePassword(newPassword);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update password';
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const refreshUserProfile = async () => {
    if (!user) return;
    
    try {
      const profile = await AuthService.getProfile(user.id);
      if (profile) {
        setUser(prev => ({
          ...prev!,
          name: profile.full_name || prev!.name,
          avatar: profile.avatar_url || prev!.avatar,
          role: (profile.role || 'user') as 'user' | 'admin'
        }));
      }
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithGoogle,
        loginWithGitHub,
        loginWithApple,
        loginWithSpotify,
        logout,
        register,
        resetPassword,
        updatePassword,
        isAuthenticated: !!user,
        refreshUserProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}